import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Upload, Banknote, Loader2, ChevronRight, Pencil, Trash2, ArrowRightLeft, CheckCircle2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import AddBankTxnDialog from "@/components/AddBankTxnDialog";
import MergeBankAccountsDialog from "@/components/MergeBankAccountsDialog";
import { toast } from "sonner";
import axios from "axios";

const COMMON_CURRENCIES = ["KYD", "USD", "EUR", "GBP", "CAD", "AUD", "BSD", "JMD", "MXN", "CNY", "JPY"];

export default function Banking() {
    const { currentOrg, features } = useAuth();
    const [accounts, setAccounts] = useState([]);
    const [txns, setTxns] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [txnOpen, setTxnOpen] = useState(false);
    const [editingTxn, setEditingTxn] = useState(null);
    const blank = { name: "", institution: "", account_type: "checking", currency: currentOrg?.currency || "KYD", opening_balance: 0 };
    const [form, setForm] = useState(blank);
    const fileRef = useRef(null);

    const loadAccounts = async () => {
        const { data } = await api.get("/bank-accounts");
        setAccounts(data.items || []);
        if (data.items?.[0] && !selectedId) setSelectedId(data.items[0].id);
    };
    const loadTxns = async () => {
        if (!selectedId) return;
        const { data } = await api.get(`/bank-transactions?bank_account_id=${selectedId}`);
        setTxns(data.items || []);
    };
    useEffect(() => { if (currentOrg) loadAccounts(); }, [currentOrg]);
    useEffect(() => { loadTxns(); }, [selectedId]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...blank, currency: currentOrg?.currency || "KYD" });
        setOpen(true);
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            name: a.name || "",
            institution: a.institution || "",
            account_type: a.account_type || "checking",
            currency: a.currency || (currentOrg?.currency || "KYD"),
            opening_balance: Number(a.opening_balance || 0),
        });
        setOpen(true);
    };

    const submit = async () => {
        try {
            const payload = { ...form, opening_balance: Number(form.opening_balance) };
            if (editing) {
                await api.patch(`/bank-accounts/${editing.id}`, payload);
                toast.success("Account updated");
            } else {
                await api.post("/bank-accounts", payload);
                toast.success("Account added");
            }
            setOpen(false);
            setEditing(null);
            setForm(blank);
            loadAccounts();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeAcct = async (a) => {
        if (!window.confirm(`Delete ${a.name}?\n\nThis cannot be undone. If the account still has any payments, expenses, or imported transactions, the delete will be blocked until you move or remove those entries first.`)) return;
        try {
            await api.delete(`/bank-accounts/${a.id}`);
            toast.success(`${a.name} deleted`);
            if (selectedId === a.id) setSelectedId(null);
            loadAccounts();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d && typeof d === "object" && d.message) {
                const pieces = [];
                if (d.invoice_payments) pieces.push(`${d.invoice_payments} invoice payment${d.invoice_payments === 1 ? "" : "s"}`);
                if (d.bill_payments) pieces.push(`${d.bill_payments} bill payment${d.bill_payments === 1 ? "" : "s"}`);
                if (d.expenses) pieces.push(`${d.expenses} expense${d.expenses === 1 ? "" : "s"}`);
                if (d.imported_transactions) pieces.push(`${d.imported_transactions} imported transaction${d.imported_transactions === 1 ? "" : "s"}`);
                toast.error(`${d.message} (${pieces.join(" · ")})`, { duration: 7000 });
            } else {
                toast.error(formatApiError(e));
            }
        }
    };

    const uploadCsv = async (file) => {
        if (!file || !selectedId) return;
        setUploading(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const orgId = localStorage.getItem("bl_org_id");
            const { data } = await axios.post(`${API_BASE}/bank-transactions/import?bank_account_id=${selectedId}`, fd, {
                withCredentials: true,
                headers: { "X-Org-Id": orgId || "" },
            });
            toast.success(`Imported ${data.inserted} transactions (${data.duplicates} duplicates skipped)`);
            loadAccounts(); loadTxns();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    const openCreateTxn = () => { setEditingTxn(null); setTxnOpen(true); };
    const openEditTxn = (t) => { setEditingTxn(t); setTxnOpen(true); };

    const removeTxn = async (t) => {
        if (!window.confirm(`Delete this transaction?\n\n${t.description}\n${fmtDate(t.date)} · ${fmtMoney(t.amount, selected?.currency || "KYD")}\n\nThis cannot be undone.`)) return;
        try {
            await api.delete(`/bank-transactions/${t.id}`);
            toast.success("Transaction deleted");
            await Promise.all([loadAccounts(), loadTxns()]);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const selected = accounts.find((a) => a.id === selectedId);

    return (
        <div>
            <PageHeader
                subtitle="Banking"
                title="Bank accounts"
                action={
                    <div className="flex gap-2">
                        {!features.bank_feeds && (
                            <span className="text-xs text-muted-foreground self-center">Auto-sync on Pro+</span>
                        )}
                        {accounts.length >= 2 && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setMergeOpen(true)}
                                className="rounded-xl border-secondary/40 text-secondary-dark hover:bg-secondary/10"
                                data-testid="merge-bank-accounts-btn"
                            >
                                <ArrowRightLeft className="h-4 w-4 mr-1.5" /> Merge
                            </Button>
                        )}
                        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(blank); } }}>
                            <DialogTrigger asChild>
                                <Button onClick={openCreate} className="bg-ocean-700 text-white rounded-xl" data-testid="new-bank-account-btn"><Plus className="h-4 w-4 mr-1.5" /> Add account</Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>{editing ? "Edit bank account" : "New bank account"}</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bank-account-name" /></div>
                                    <div><Label>Institution</Label><Input className="mt-1.5" value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} data-testid="bank-account-institution" /></div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <Label>Type</Label>
                                            <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                                                <SelectTrigger className="mt-1.5" data-testid="bank-account-type"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="checking">Checking</SelectItem>
                                                    <SelectItem value="savings">Savings</SelectItem>
                                                    <SelectItem value="credit_card">Credit card</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Currency</Label>
                                            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                                                <SelectTrigger className="mt-1.5" data-testid="bank-account-currency"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {Array.from(new Set([currentOrg?.currency || "KYD", form.currency, ...COMMON_CURRENCIES])).filter(Boolean).map((c) => (
                                                        <SelectItem key={c} value={c}>{c}{c === (currentOrg?.currency || "KYD") ? " · Base" : ""}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div><Label>Opening balance</Label><Input className="mt-1.5" type="number" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} data-testid="bank-account-opening-balance" /></div>
                                    {editing && Number(editing.opening_balance) !== Number(form.opening_balance) && (
                                        <div className="text-xs text-secondary-dark bg-secondary/10 rounded-lg px-3 py-2">
                                            The running balance will be recalculated to keep your ledger consistent. New balance: <span className="font-mono font-medium">{fmtMoney(Number(form.opening_balance || 0) + (Number(editing.balance) - Number(editing.opening_balance)), form.currency)}</span>
                                        </div>
                                    )}
                                </div>
                                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-ocean-700 text-white" data-testid="submit-bank-account">{editing ? "Save changes" : "Save"}</Button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />
            {accounts.length === 0 ? (
                <EmptyState icon={Banknote} title="No bank accounts yet" description="Add an account to track cash and import statements." />
            ) : (
                <div className="grid lg:grid-cols-[280px,1fr] gap-6">
                    <div className="space-y-2">
                        {accounts.map((a) => (
                            <div
                                key={a.id}
                                data-testid={`bank-account-${a.id}`}
                                className={`w-full text-left rounded-2xl border p-4 transition-colors ${
                                    a.id === selectedId ? "border-secondary bg-secondary/5" : "border-sand-300 bg-white hover:border-secondary/40"
                                }`}
                            >
                                <button onClick={() => setSelectedId(a.id)} className="w-full text-left">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-xs uppercase tracking-wider text-muted-foreground truncate">{a.institution || a.account_type}</div>
                                            <div className="font-medium text-ocean-700 mt-1 truncate">{a.name}</div>
                                        </div>
                                        <span className="text-[10px] font-medium tracking-wide text-secondary-dark bg-secondary/10 rounded-md px-1.5 py-0.5 shrink-0" data-testid={`bank-account-currency-${a.id}`}>
                                            {a.currency}
                                        </span>
                                    </div>
                                    <div className="mt-2 font-mono text-sm text-ocean-700">{fmtMoney(a.balance, a.currency)}</div>
                                </button>
                                <div className="mt-3 flex items-center justify-between gap-2">
                                    <Link
                                        to={`/app/banking/${a.id}`}
                                        data-testid={`view-ledger-${a.id}`}
                                        className="inline-flex items-center gap-1.5 text-xs font-medium text-secondary-dark hover:text-secondary hover:underline"
                                    >
                                        View ledger <ChevronRight className="h-3.5 w-3.5" />
                                    </Link>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                                            className="p-1.5 rounded-md hover:bg-sand-100 text-muted-foreground hover:text-ocean-700 transition-colors"
                                            aria-label={`Edit ${a.name}`}
                                            data-testid={`edit-bank-account-${a.id}`}
                                            title="Edit account"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); removeAcct(a); }}
                                            className="p-1.5 rounded-md hover:bg-coral/10 text-muted-foreground hover:text-coral-dark transition-colors"
                                            aria-label={`Delete ${a.name}`}
                                            data-testid={`delete-bank-account-${a.id}`}
                                            title="Delete account"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs uppercase tracking-wider text-muted-foreground">Transactions</div>
                                <h3 className="font-heading text-xl text-ocean-700">{selected?.name || "—"}</h3>
                            </div>
                            <div className="flex gap-2">
                                <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => uploadCsv(e.target.files?.[0])} data-testid="csv-import-input" />
                                <Button
                                    onClick={openCreateTxn}
                                    disabled={!selected}
                                    className="bg-ocean-700 text-white rounded-xl"
                                    data-testid="add-bank-txn-btn"
                                >
                                    <Plus className="h-4 w-4 mr-1.5" /> Add transaction
                                </Button>
                                <Button variant="outline" className="rounded-xl border-sand-300" onClick={() => fileRef.current?.click()} disabled={uploading || !selected}>
                                    {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Import CSV
                                </Button>
                            </div>
                        </div>
                        <div className="mt-5 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs uppercase text-muted-foreground">
                                    <tr>
                                        <th className="text-left py-2">Date</th>
                                        <th className="text-left py-2">Description</th>
                                        <th className="text-left py-2">Category</th>
                                        <th className="text-left py-2">Reference</th>
                                        <th className="text-right py-2">Amount</th>
                                        <th className="py-2"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-sand-200">
                                    {txns.map((t) => (
                                        <tr key={t.id} className="hover:bg-sand-50/50" data-testid={`bank-txn-row-${t.id}`}>
                                            <td className="py-3 text-muted-foreground">{fmtDate(t.date)}</td>
                                            <td className="py-3 text-ocean-700">
                                                <span className="inline-flex items-center gap-1.5">
                                                    {t.description}
                                                    {t.reconciled && (
                                                        <Badge className="bg-secondary/15 text-secondary-dark border-0 text-[10px] uppercase tracking-wider">
                                                            <CheckCircle2 className="h-3 w-3 mr-0.5" /> Matched
                                                        </Badge>
                                                    )}
                                                </span>
                                            </td>
                                            <td className="py-3"><span className="inline-flex items-center rounded-full bg-sand-200 text-ocean-700 px-2.5 py-0.5 text-xs">{t.category || "—"}</span></td>
                                            <td className="py-3 text-muted-foreground text-xs">{t.reference || "—"}</td>
                                            <td className={`py-3 font-mono text-right ${t.amount < 0 ? "text-coral-dark" : "text-secondary-dark"}`}>{fmtMoney(t.amount, selected?.currency || "KYD")}</td>
                                            <td className="py-3 text-right whitespace-nowrap">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditTxn(t)}
                                                    title="Edit transaction"
                                                    data-testid={`edit-bank-txn-${t.id}`}
                                                >
                                                    <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => removeTxn(t)}
                                                    title="Delete transaction"
                                                    data-testid={`delete-bank-txn-${t.id}`}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {txns.length === 0 && <tr><td colSpan="6" className="py-12 text-center text-muted-foreground">No transactions yet. Add one manually or import a CSV to get started.</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <AddBankTxnDialog
                open={txnOpen}
                onOpenChange={(v) => { setTxnOpen(v); if (!v) setEditingTxn(null); }}
                account={selected}
                editing={editingTxn}
                onSaved={() => Promise.all([loadAccounts(), loadTxns()])}
            />

            <MergeBankAccountsDialog
                open={mergeOpen}
                onOpenChange={setMergeOpen}
                accounts={accounts}
                onSelectCanonical={setSelectedId}
                onMerged={loadAccounts}
            />
        </div>
    );
}
